import { ErpatApiError, type ApiResult } from '@erpat/sdk';
import {
  erpatClient,
  clearSession,
  getSessionEpoch,
  adoptPersistedTokens,
  accessTokenExpiringWithin,
  REQUEST_TIMEOUT_MS,
  SESSION_CANCELLED_MESSAGE,
} from '@/services/api/erpat-client';
import { SECURITY_CHALLENGE_MESSAGE, isSecurityChallenge } from '@/utils/edge-challenge';

/**
 * Re-exported beside its sibling predicates for callers that already import
 * this module. The definition — and the import path for anything a spec may
 * load with this module mocked — is utils/edge-challenge.ts.
 */
export { isSecurityChallenge };

/**
 * Shared helpers around the SDK transport.
 *
 * The ERPat API wraps every payload in an envelope; the SDK already unwraps it
 * to `ApiResult.data` (typed `unknown`). These helpers narrow that `unknown`
 * defensively — the published OpenAPI only specifies the envelope, not
 * field-level shapes, so we read the most likely keys with fallbacks rather
 * than trusting one exact shape.
 */

/** Normalised pagination, mapped from the API's snake_case `meta`. */
export interface Pagination {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

/** A plain JSON object — the lowest common denominator for an unknown payload. */
export type Json = Record<string, unknown>;

/** Narrow an unknown value to a record (or `{}`). */
export function asRecord(value: unknown): Json {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Json) : {};
}

/**
 * Coerce an unknown `data` payload into an array. The ERPat API wraps every
 * collection under a domain-named key — e.g. `{ payslips: [...], meta }`,
 * `{ leaves: [...] }`, `{ contacts: [...] }` — so after the common
 * `items`/`data` keys we fall back to the first array-valued property that
 * isn't the nested `meta`.
 */
export function toList<T = Json>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  const rec = asRecord(data);
  for (const key of ['items', 'data', 'results', 'records', 'rows', 'list']) {
    if (Array.isArray(rec[key])) return rec[key] as T[];
  }

  for (const key of ['data', 'result', 'payload', 'response', 'items']) {
    const nested = rec[key];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const nestedItems = toList<T>(nested);
      if (nestedItems.length > 0) return nestedItems;
    }
  }

  for (const [key, value] of Object.entries(rec)) {
    if (key !== 'meta' && Array.isArray(value)) return value as T[];
  }
  return [];
}

/**
 * Unwrap a single-object response from its domain key. ERPat returns single
 * resources as `{ profile: {...} }`, `{ payslip: {...} }`, `{ dashboard: {...} }`,
 * etc. Returns the first object found under one of `keys`, else the record
 * itself (for already-flat payloads like `capabilities`).
 */
export function domainBody(data: unknown, keys: string[]): Json {
  const rec = asRecord(data);
  for (const k of keys) {
    const v = rec[k];
    if (v && typeof v === 'object' && !Array.isArray(v)) return v as Json;
  }
  return rec;
}

/** Map the SDK's `meta` (snake_case) into a normalised {@link Pagination}. */
export function toPagination(meta: Record<string, unknown> | undefined, fallbackCount = 0): Pagination {
  const m = asRecord(meta);
  const num = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
  return {
    page: num(m.page, 1),
    perPage: num(m.per_page ?? m.perPage, fallbackCount || 20),
    total: num(m.total, fallbackCount),
    totalPages: num(m.total_pages ?? m.totalPages, 1),
  };
}

/** Read the first present key from a record, with a typed fallback. */
export function pick<T>(rec: Json, keys: string[], fallback: T): T {
  for (const k of keys) {
    const v = rec[k];
    if (v !== undefined && v !== null) return v as T;
  }
  return fallback;
}

let onExpire: (() => void) | null = null;

/**
 * Register a callback fired when a session cannot be recovered (refresh failed
 * on a 401). The session store wires this to a sign-out + redirect.
 */
export function setSessionExpiredHandler(fn: () => void): void {
  onExpire = fn;
}

/**
 * Tear down an unrecoverable session: clear cached/persisted tokens and notify
 * the registered handler (sign-out + redirect). Exported so the proactive
 * background refresher can trigger the same teardown when its refresh fails.
 */
export function handleSessionExpired(): void {
  clearSession();
  onExpire?.();
}

/**
 * True when `err` is a request a sign-out cancelled (`cancelSessionRequests()`),
 * as raised by `sessionFetch()` or after the SDK / an upload helper wrapped it.
 *
 * Not a failure: the user chose to leave. Nothing about it should be retried,
 * reported as "offline", or treated as the session expiring.
 */
export function isSessionCancelled(err: unknown): boolean {
  if (err instanceof ErpatApiError) {
    return err.status === 0 && err.message === SESSION_CANCELLED_MESSAGE;
  }
  return err instanceof Error && err.name === 'AbortError' && err.message === SESSION_CANCELLED_MESSAGE;
}

/**
 * True when the answer was not JSON at all (the SDK's `invalid_response`) —
 * something served a web page where this API only ever answers JSON: an edge's
 * block page, a captive portal, a WAF, or the `/install` page an under-migrated
 * tenant is redirected to.
 *
 * Whatever its status, it is not ERPat's verdict on the request: ERPat's own
 * refusals are JSON envelopes. A challenge that carried the edge's marker never
 * gets this far — `sessionFetch()` rejects it first (see `isSecurityChallenge`).
 */
export function isWebPageResponse(err: unknown): boolean {
  return err instanceof ErpatApiError && err.code === 'invalid_response';
}

/**
 * True when the answer did not carry ERPat's own error envelope: a web page
 * (the SDK's `invalid_response`), or a bare status with an empty / non-envelope
 * body (the SDK's synthetic `http_<status>` code).
 *
 * ERPat's refusals ALWAYS carry an envelope code (`invalid_grant`,
 * `invalid_token`, …). So a 401/403 without one was written by something in
 * front of ERPat — an edge block response configured as JSON or left empty, a
 * proxy, a captive portal — and is not a verdict on the token: the token was
 * never looked at. Wider than {@link isWebPageResponse} on purpose; a block
 * response does not have to be a page.
 */
export function isForeignResponse(err: unknown): boolean {
  return err instanceof ErpatApiError && (err.code === 'invalid_response' || err.code.startsWith('http_'));
}

/**
 * Tear the session down after a token refresh failed — but ONLY on an answer
 * ERPat itself gave about the refresh token. Nothing else can end a session:
 *
 *  - The session was already ending: a sign-out cancelled the refresh, or a
 *    teardown bumped the epoch while it ran. Declaring that "expired" would run
 *    the sign-out a second time, mid-way through the first, and wipe the token
 *    the logout request still needs.
 *  - The answer was not ERPat's: an edge's security check intercepted the
 *    refresh ({@link isSecurityChallenge}), or it came back without ERPat's
 *    envelope — a web page or a bare status ({@link isForeignResponse}). The
 *    refresh token was not rejected; it was never looked at.
 *  - The refresh never got an answer at all (status 0): no network, DNS/TLS,
 *    the SDK's own timeout — or an opaque cross-origin failure, WHICH IS
 *    EXACTLY WHAT AN EDGE CHALLENGE LOOKS LIKE from the deployed PWA and the
 *    native shells: the preflight is challenged, `fetch` rejects with a
 *    TypeError, and no header is readable. Silence is not a refusal.
 *
 * `refreshTick()` (token-refresh.ts) has always swallowed that last kind; the
 * reactive path now agrees, so a dropped connection — or a challenge — at the
 * moment a token needs rotating no longer signs the user out. The caller gets
 * its original 401 and the next request, or the next tick, tries again.
 *
 * What still ends the session: ERPat's own refusal (an envelope code such as
 * `invalid_grant`), and anything that is not an `ErpatApiError` at all — e.g.
 * the SDK's "no refresh_token available" Error, which no retry can cure.
 *
 * @param epoch the session generation captured before the request that 401'd.
 */
export function expireAfterFailedRefresh(err: unknown, epoch: number): void {
  if (isSessionCancelled(err) || getSessionEpoch() !== epoch) return;
  if (isSecurityChallenge(err) || isForeignResponse(err)) return;
  // Never answered — not a verdict on the token (see above).
  if (err instanceof ErpatApiError && err.status === 0) return;
  handleSessionExpired();
}

let onRefreshed: (() => void) | null = null;

/**
 * Register a callback fired after every successful access-token rotation
 * (reactive 401 retry or the proactive background refresher). The user store
 * wires this to re-fetch the signed-in user's identity so it tracks the latest
 * server-side profile. Registered via a callback (not a direct import) to keep
 * this transport module free of a store → service → transport import cycle.
 */
export function setTokenRefreshedHandler(fn: () => void): void {
  onRefreshed = fn;
}

let refreshInFlight: Promise<void> | null = null;

/**
 * Cross-tab lock name serializing token rotation. The server consumes a
 * refresh token on first use and treats a replay as theft (revokes the whole
 * session family), so two TABS rotating concurrently would sign the user out.
 * The in-tab single-flight below can't cover that; the Web Lock does.
 */
const REFRESH_LOCK_NAME = 'syntry.token-refresh';

/**
 * An adopted (other-tab-rotated) access token must outlive the caller's retried
 * request to be worth skipping our own rotation for; otherwise rotate anyway.
 */
const ADOPTED_TOKEN_FRESH_SEC = 60;

/** Run `fn` under a cross-tab exclusive lock (no-op where Web Locks is absent). */
function withCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) return fn();
  return locks.request(REFRESH_LOCK_NAME, fn) as Promise<T>;
}

/**
 * Single-flight access-token refresh.
 *
 * Both the reactive 401 retry below and the proactive background refresher
 * (`token-refresh.ts`) funnel through here. The server **rotates** the refresh
 * token on every refresh, so two concurrent `auth.refresh()` calls would race —
 * the first rotation invalidates the token the second one is still using (and
 * the server then revokes the whole family as suspected token theft). Sharing
 * one in-flight promise collapses concurrent attempts in THIS tab; the
 * cross-tab Web Lock serializes rotations ACROSS tabs, and the adoption
 * fast-path inside the lock skips the round-trip entirely when another tab
 * already rotated while we were waiting.
 */
export function refreshSession(): Promise<void> {
  if (!refreshInFlight) {
    // Capture the session generation so we can detect a sign-out / expiry that
    // races this in-flight refresh.
    const epoch = getSessionEpoch();
    refreshInFlight = withCrossTabLock(async () => {
      // Signed out while we queued behind another tab's rotation — do nothing.
      if (getSessionEpoch() !== epoch) return;
      // Another tab may have rotated the pair while we waited for the lock (or
      // while this tab's client held a stale in-memory pair). Adopt it; if the
      // adopted access token is still comfortably fresh there is nothing to do —
      // presenting OUR now-consumed refresh token would revoke the session.
      if (adoptPersistedTokens() && !accessTokenExpiringWithin(ADOPTED_TOKEN_FRESH_SEC)) {
        return;
      }
      await erpatClient.auth.refresh();
      // The SDK has already written the rotated tokens into the client. If a
      // teardown happened while we were awaiting, those tokens would silently
      // resurrect the session (hasAccessToken() → true, router guard re-admits
      // a "signed-out" user). Teardown must win: re-clear and bail.
      if (getSessionEpoch() !== epoch) {
        clearSession();
        return;
      }
      // Fire-and-forget identity sync; the handler must never break a refresh.
      try {
        onRefreshed?.();
      } catch {
        /* swallow — a failed identity sync is non-fatal to the token refresh */
      }
    }).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Run an SDK call, transparently refreshing the access token once on a 401 and
 * retrying. If ERPat itself refuses the refresh, the session is treated as
 * expired; a refresh that merely did not get through leaves the session alone
 * (see {@link expireAfterFailedRefresh}). Either way the original 401 is what
 * the caller sees.
 */
export async function withAuthRetry<T>(call: () => Promise<ApiResult<T>>): Promise<ApiResult<T>> {
  const epoch = getSessionEpoch();
  try {
    return await call();
  } catch (err) {
    if (err instanceof ErpatApiError && err.status === 401 && erpatClient.getRefreshToken()) {
      try {
        await refreshSession();
      } catch (refreshErr) {
        expireAfterFailedRefresh(refreshErr, epoch);
        throw err;
      }
      return call();
    }
    throw err;
  }
}

/**
 * The 401 codes that mean "the ACCESS TOKEN is no good" — the closed set the
 * End-User JWT middleware answers with before any action runs. Only these are
 * worth a refresh.
 *
 * Every other 401 is an answer about a CREDENTIAL the user just typed: a wrong
 * password (`invalid_credentials`) or a wrong Security PIN
 * (`invalid_security_pin`). Refreshing on those would rotate the refresh token
 * on every typo, and a refresh that then failed would sign the user out for
 * mistyping.
 */
export const TOKEN_401_CODES: ReadonlySet<string> = new Set([
  'missing_token',
  'invalid_token',
  'unknown_kid',
  'expired_token',
  'invalid_signature',
  'revoked_token',
  'tenant_mismatch',
]);

/**
 * {@link withAuthRetry} for calls whose body carries a credential — a password
 * or a Security PIN (`/me/confidential/verify`, `/me/security-pin`,
 * `/me/profile/government-ids`, `/me/password`).
 *
 * Refreshes and retries ONCE, exactly as `withAuthRetry` does, but only when the
 * 401's code is in {@link TOKEN_401_CODES}. A credential 401 — and every other
 * error — is rethrown untouched, so the caller can tell the user their PIN or
 * password was wrong without the session paying for it.
 */
export async function withCredentialRetry<T>(call: () => Promise<T>): Promise<T> {
  const epoch = getSessionEpoch();
  try {
    return await call();
  } catch (err) {
    if (
      err instanceof ErpatApiError &&
      err.status === 401 &&
      TOKEN_401_CODES.has(err.code) &&
      erpatClient.getRefreshToken()
    ) {
      try {
        await refreshSession();
      } catch (refreshErr) {
        // Same exemptions as `withAuthRetry`: a refresh that was cancelled, that
        // never got an answer, or that something in front of ERPat answered in
        // its place, is not an expired session.
        expireAfterFailedRefresh(refreshErr, epoch);
        throw err;
      }
      return call();
    }
    throw err;
  }
}

/** Resolve an {@link ApiResult} to its `data`, with the 401 refresh-retry applied. */
export async function unwrap<T = unknown>(call: () => Promise<ApiResult<T>>): Promise<T> {
  const res = await withAuthRetry(call);
  return res.data;
}

/** Resolve an {@link ApiResult} to `{ items, pagination }` for list endpoints. */
export async function unwrapList<T = Json>(
  call: () => Promise<ApiResult>,
): Promise<{ items: T[]; pagination: Pagination }> {
  const res = await withAuthRetry(call);
  const items = toList<T>(res.data);
  // Pagination lives at the envelope `meta` OR nested at `data.meta`.
  const meta = res.meta ?? (asRecord(res.data).meta as Record<string, unknown> | undefined);
  return { items, pagination: toPagination(meta, items.length) };
}

/**
 * `details` keys that are FOR INTEGRATORS, not for the toast. Each names or
 * carries machine-readable context the refusal's own `message` already states
 * in words:
 *
 *  - `required_permit` — an internal permission key (`attendance_self_manage`).
 *    Appended, a permission refusal read "…to approve it.: attendance_self_manage".
 *  - `reason_code` / `filing_window` — a leave filing refusal sends
 *    `{ reason_code: 'BACKDATE_WINDOW_EXCEEDED', filing_window: { today,
 *    earliest_start_date, min_start_date, allow_backdate, lock_days, … } }`
 *    beside a complete sentence. Flattened, the toast became
 *    "<sentence>: BACKDATE_WINDOW_EXCEEDED 2026-09-25 …" — a code the user
 *    cannot act on and a bare date with no label. The window is for the form
 *    to pin its date picker (`services/api/leave-filing.ts`), not for prose.
 *  - `accept` / `max_file_bytes` / `max_files` / `file` / `reason` — a leave
 *    supporting-document refusal (HumanResource ≥ 1.95.0) sends the accepted
 *    extensions, the limits, the offending file's NAME, and a `reason` that is
 *    developer English ("references a file that is no longer staged") or a
 *    policy code (`store_absent`). Flattened, a toast read "<sentence>: pdf jpg"
 *    or "<sentence>: cert.exe". The app words these refusals from their `code`
 *    (`services/api/leave-attachments.ts`). Skipped at every depth like the keys
 *    above, which also drops the shared upload's developer-only `fields.file`
 *    ("is required (multipart field …)"). A `fields.reason` adds nothing to the
 *    toast either: `POST /me/leaves` sends one for the leave's own reason, which
 *    the request sheet requires before it posts (at most 200 characters), and
 *    the asset-endorsement reject sends one beside a message that already says
 *    "A reason is required.".
 *  - `extension` / `allowed` — the shared upload staging refuses a file type, or
 *    a name it cannot use, with `{ extension }` (messages, groups, tickets, to-do
 *    comments) and a form's own type list with
 *    `{ extension, allowed }`. Flattened, "This file type is not allowed." read
 *    "…allowed.: exe". Machine context, like `accept`: elsewhere `allowed` only
 *    carries a 405's method list or an enum list (`invalid_status`).
 */
const INTERNAL_DETAIL_KEYS: ReadonlySet<string> = new Set([
  'required_permit',
  'reason_code',
  'filing_window',
  'accept',
  'max_file_bytes',
  'max_files',
  'file',
  'reason',
  'extension',
  'allowed',
]);

/**
 * Flatten an error envelope's `details` into a short human string.
 *
 * The API returns per-field validation messages here while `message` stays
 * generic ("Validation failed", "Internal error"), so dropping them left the
 * user with a message that named no cause.
 *
 * Keys in {@link INTERNAL_DETAIL_KEYS} are skipped — they carry codes and
 * dates for a client to branch on, and the refusal's `message` already says
 * what the user can do.
 */
function detailText(details: unknown): string {
  const parts: string[] = [];

  const push = (value: unknown): void => {
    if (typeof value === 'string' && value.trim()) parts.push(value.trim());
    else if (Array.isArray(value)) value.forEach(push);
    else if (value && typeof value === 'object') {
      for (const [key, nested] of Object.entries(value)) {
        if (!INTERNAL_DETAIL_KEYS.has(key)) push(nested);
      }
    }
  };
  push(details);

  // Two is enough for a toast; more turns into a wall of text.
  return parts.slice(0, 2).join(' ');
}

/** Turn any thrown value into a user-facing message. */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  // Before the status-0 branch below, which would call it a lost connection.
  if (isSessionCancelled(err)) return SESSION_CANCELLED_MESSAGE;
  // Likewise status 0 — but the network is fine: an edge's security check
  // answered in the server's place, and only an administrator can lift it.
  if (isSecurityChallenge(err)) return SECURITY_CHALLENGE_MESSAGE;
  if (err instanceof ErpatApiError) {
    if (err.status === 0) return 'No connection. Check your network and try again.';

    // The SDK raises `invalid_response` when the body would not parse as JSON.
    // Against this API that is not a transport hiccup, it is a TENANT fault: the
    // CI3 `check_database` hook 302-redirects to `/install` when a migration is
    // missing, so an authenticated endpoint answers 200 with an HTML page. The
    // SDK discards the body, so the code plus a non-error status is all we have
    // to go on — and it is enough, because a JSON API that returns 200-not-JSON
    // has served a web page. Say that, instead of leaking "Response was not
    // valid JSON (status 200)" at the user, who can do nothing with it.
    if (err.code === 'invalid_response') {
      // A 403 that is a web page is a block page with none of the edge's
      // markers on it (those are caught in `sessionFetch()`): ERPat refuses in
      // JSON. "Try again" would be the wrong advice — nothing the user retries
      // gets past it.
      if (err.status === 403) {
        return 'The server refused this request with a web page instead of an answer — a security check may be blocking the app. Please contact your administrator.';
      }
      return err.status < 400
        ? 'The server returned a web page instead of data. Your company\'s ERPat database may need updating — please contact your administrator.'
        : 'The server sent a response the app could not read. Please try again, or contact your administrator if it continues.';
    }

    const base = err.message || fallback;
    const detail = detailText(err.details);

    // A server-side fault says nothing actionable on its own, so surface the
    // code and request id — together they are what identifies the entry in the
    // backend log.
    let ref = '';
    if (err.status >= 500) {
      const marks = [err.code, err.requestId].filter(Boolean);
      ref = marks.length ? ` (${marks.join(' · ')})` : ` (HTTP ${err.status})`;
    }

    return detail ? `${base}: ${detail}${ref}` : `${base}${ref}`;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * Slack before a measured elapsed time may be read as "the SDK's abort fired".
 * The abort can only land at or AFTER the ceiling — its timer starts inside
 * `request()`, strictly later than the caller's clock — so this only absorbs a
 * coarse clock, and the two populations are nowhere near the boundary: a genuine
 * transport failure rejects in single-digit milliseconds.
 */
const TIMEOUT_ATTRIBUTION_SLACK_MS = 500;

/**
 * A user-facing message for a SIGN-IN failure, with the two transport faults the
 * SDK cannot tell apart pulled back apart.
 *
 * `ErpatApiError('network_error', …, 0)` is everything the SDK says about a failed
 * fetch: no network, DNS/TLS failure, nothing listening, and its own 30 s abort
 * all arrive identically (it wraps the rejection and keeps only `message`).
 * {@link errorMessage} answers all of them with "No connection. Check your
 * network and try again." — which is actively wrong for the abort: the connection
 * is fine, the server is simply not answering, and that copy sends the user off
 * to fix the one thing that is not broken.
 *
 * `elapsedMs`, measured by the caller around the SDK call, is the discriminator:
 * at the ceiling, the abort is the only thing that can have ended the request.
 * `navigator.onLine === false` settles the rest; where the browser claims to be
 * online and the request died fast, the honest answer names the server, not the
 * user's network.
 *
 * Deliberately NOT folded into `errorMessage()`: every other caller of that is a
 * page-level read with no timing to offer, and a `status === 0` there really does
 * mean "you are probably offline".
 */
export function describeAuthError(
  err: unknown,
  elapsedMs: number,
  fallback = 'Unable to sign in. Please try again.',
): string {
  // First: a challenged sign-in is status 0 as well, and every branch below
  // would blame the server's speed, the user's network, or "right now".
  if (isSecurityChallenge(err)) return SECURITY_CHALLENGE_MESSAGE;
  if (err instanceof ErpatApiError && err.status === 0) {
    if (elapsedMs >= REQUEST_TIMEOUT_MS - TIMEOUT_ATTRIBUTION_SLACK_MS) {
      return 'The server took too long to answer. Please try again.';
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return 'No connection. Check your network and try again.';
    }
    return "Couldn't reach the server right now. Please try again in a moment.";
  }
  return errorMessage(err, fallback);
}
